import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import { COLORS, ComponentS, ComponentTextStyle, ComponentXL, ComponentXLStyling, Tag, TagVariants } from '../../index';
import { ReactNode } from 'react';

export interface VerticalCardMiddleSectionProps {
  /** Color of the band to be shown on the top of middle section. */
  colorBandColor?: string;
  /** Icon to be shown on the left side of the categoryLabel. */
  categoryIcon?: React.ReactNode;
  /** Label to be shown at the top part of the middle section. */
  categoryLabel?: string;
  /** Main title of the Card component. */
  title: string;
  /** Description of the Card. */
  description?: string;
  /** Tags to be shown under the description. */
  tags?: VerticalCardTag[];
  /** Second row of tags if needed. */
  row2Tags?: VerticalCardTag[];
  /** This property is set by Card component itself and is based on 'disabled' flag of CardProps. */
  disabled?: boolean;
  /** Top level Id of Card component */
  componentId?: string;
}

export interface VerticalCardTag {
  label: string;
  variant?: TagVariants;
  icon?: ReactNode
}

export const VerticalCardMiddleSectionContainer = styled.div`
  display: flex;
  flex-grow: 1;
  width: calc(100% - 32px);
  position: relative;
  flex-direction: column;
  padding: 16px;
  gap: 8px;

  .descriptionBlock {
    padding: 2px 0;
  }

  .titleBlock {
    padding: 2px 0;
  }
`;

export const VerticalCardMiddleSectionColorBandStyles = styled.div<{ $color: string }>`
  position: absolute;
  width: 100%;
  top: 0px;
  left: 0px;
  height: 8px;
  background-color: ${(props) => props.$color};
`;

export const VerticalCardMiddleSectionTagsStyles = styled.div`
  display: flex;
  flex-direction: row;
  padding: 4px 0;
  gap: 4px;
  flex-wrap: wrap;
`;

export const VerticalCardMiddleSectionCategoryStyles = styled.div<{ $extraTopMargin: boolean; $bottomMargin: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: none;
  gap: 6px;

  svg {
    color: ${props => COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme)};
    flex-grow: 0;
    flex-shrink: 0;
  }
`;

export const VerticalCardMiddleSectionTitle = styled.h3<{ $disabled?: boolean }>`
  ${props => ComponentXLStyling(ComponentTextStyle.Bold, props.$disabled 
    ? COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, props.theme)
    : COLORS.generateToken({componentType:'text',defaultVariant:'default'}, props.theme)
  )}
`;

const VerticalCardMiddleSection: React.FunctionComponent<VerticalCardMiddleSectionProps> = ({
  colorBandColor,
  categoryIcon,
  categoryLabel,
  title,
  description,
  tags,
  row2Tags,
  disabled,
  componentId
}: VerticalCardMiddleSectionProps) => {
  const theme = useTheme();
  
  return (
    <VerticalCardMiddleSectionContainer data-testid={'card-middleSection'} >
      {colorBandColor && <VerticalCardMiddleSectionColorBandStyles $color={colorBandColor} />}
      {
        (categoryIcon || categoryLabel) &&
        <VerticalCardMiddleSectionCategoryStyles $extraTopMargin={Boolean(colorBandColor)} $bottomMargin={Boolean(categoryIcon || categoryLabel)}>
          {categoryIcon}
          <ComponentS textStyle={ComponentTextStyle.Bold} color={COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, theme)}>
            {categoryLabel}
          </ComponentS>
        </VerticalCardMiddleSectionCategoryStyles>
      }
      <VerticalCardMiddleSectionTitle 
        id={`${componentId}-title`} 
        className="titleBlock"
        $disabled={disabled}>
        {title}
      </VerticalCardMiddleSectionTitle>
      {description && (
        <ComponentS className="descriptionBlock" color={COLORS.generateToken({componentType:'text',defaultVariant:'subtle'}, theme)}>
          {description}
        </ComponentS>
      )}
      {tags && (
        <VerticalCardMiddleSectionTagsStyles>
          {tags.map((x, index) => (
            <Tag key={`${x.label}_${index}`} label={x.label} variant={x.variant ?? 'neutral'} icon={x.icon} />
          ))}
        </VerticalCardMiddleSectionTagsStyles>
      )}
      {row2Tags && (
        <VerticalCardMiddleSectionTagsStyles>
          {row2Tags.map((x, index) => (
            <Tag key={`${x.label}_${index}`} label={x.label} variant={x.variant ?? 'neutral'} icon={x.icon} />
          ))}
        </VerticalCardMiddleSectionTagsStyles>
      )}
    </VerticalCardMiddleSectionContainer>
  );
};

export default VerticalCardMiddleSection;
